import React from "react";
import { connect } from "react-redux";
import { Briefcase, Clock, DollarSign, MapPin, Sparkles } from "lucide-react";
import { Card, Input } from "antd";
import dayjs from "dayjs";
import RichText from "components/fields/RichText";

const BulletList = ({ items = [], gradient }) =>
  items.map((item, idx) => (
    <div key={idx} className="flex items-center gap-2 mb-1 py-1">
      <div
        className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-gradient-to-r ${gradient}`}
      >
        <div className="w-2 h-2 rounded-full bg-white"></div>
      </div>
      <p className="text-base text-gray-700">{item}</p>
    </div>
  ));

const JobDescription = ({ recordData, onApplyHandler }) => {

  const meta = [
    { icon: Briefcase, label: recordData?.department_name, wrapper: "gradient-light-purple", iconColor: "text-purple-600" },
    { icon: MapPin, label:recordData?.location_name, wrapper: "gradient-light-emerald", iconColor: "text-emerald-600" },
    { icon: Clock, label: recordData?.employment_type, wrapper: "gradient-light-blue", iconColor: "text-blue-600" },
    { icon: DollarSign, label: recordData?.jpo_salary_range, wrapper: "gradient-light-amber", iconColor: "text-amber-600" },
  ];

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <div className="relative group">
        <div className="card-shadow"></div>
        <div className="group-hover:opacity-30" />
        <Card className="card-border-effect !p-0">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between p-1">
            <div className="flex-1 space-y-5">
              <div className="!inline-flex gap-2 icon-effect gradient-light-purple px-3 py-1 !rounded-full">
                <Sparkles className="w-3 h-3 text-purple-600" />
                <span className="text-purple-900 text-base">Featured Position</span>
              </div>

              <h1 className="text-gray-800 text-2xl font-medium">{recordData?.jpo_title}</h1>

              <div className="flex flex-wrap gap-4 text-base text-gray-700">
                {meta.map(({ icon: Icon, label, wrapper, iconColor }) => (
                  <div key={label} className="flex items-center gap-2">
                    <div className={`w-10 h-10 icon-effect ${wrapper}`}>
                      <Icon className={`w-5 h-5 ${iconColor}`} />
                    </div>
                    <span>{label}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-3 pt-2">
                <span className="status-indicator icon-effect gradient-primary">{recordData?.employment_type}</span>
                <span className="text-gray-600 text-base">
                  Posted on {dayjs(recordData?.jpo_posting_date_from).format("MMMM D, YYYY")}
                </span>
              </div>
            </div>

            { recordData?.already_applied ?
              <div className="bg-gray-100 px-5 py-2 font-semibold rounded text-gray-500">
                Pending Application
              </div>
              :
              <button onClick={onApplyHandler} className="icon-effect gradient-primary px-5 py-2 font-semibold">
                Apply for this position
              </button>
            }
            
          </div>
        </Card>
      </div>

      {/* About the Role */}
      <div className="relative group">
        <div className="hover-effect group-hover:opacity-20 transition-opacity" />
        <Card className="border-2 rounded-xl !p-1 hover:border-purple-100">
          <h2 className="text-gray-800 mb-4 text-xl font-medium">About the Role</h2>
              <RichText className="text-base text-gray-700" html={recordData?.jpo_job_description}/>
        </Card>
      </div>

      {/* Responsibilities */}
      <div className="relative group">
        <div className="hover-effect-emerald group-hover:opacity-20 transition-opacity" />
        <Card className="border-2 rounded-xl !p-1 hover:border-emerald-100">
          <h2 className="text-gray-800 mb-4 text-xl font-medium">Responsibilities</h2>
          <BulletList items={recordData?.jpo_key_responsibilities} gradient="from-purple-500 to-emerald-500" />
        </Card>
      </div>

      {/* Requirements */}
      <div className="relative group">
        <div className="hover-effect-pink group-hover:opacity-10 transition-opacity" />
        <Card className="border-2 rounded-xl !p-1 hover:border-purple-100">
          <h2 className="text-gray-800 mb-4 text-xl font-medium">Requirements</h2>
          <BulletList items={recordData?.jpo_preferred_qualifications} gradient="from-purple-500 to-pink-500" />
        </Card>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  recordData: state.jobPost.jobPostDetails.data,
});

export default connect(mapStateToProps, null)(JobDescription);